<template>
  <teleport to="body">
    <div class="cropper-page" role="dialog" aria-modal="true" aria-label="صفحة اضافة الصورة">
      <div class="cropper-card">
        <div class="cropper-header">
          <h3 class="cropper-title">اضافة الصورة</h3>
          <p class="cropper-subtitle">حرّك الصورة أو كبّرها وحدد الجزء المناسب ثم اضغط حدد و أضف</p>
        </div>

        <div class="cropper-stage">
          <img ref="img" :src="src" alt="صورة الاضافة" class="cropper-image">
        </div>

        <div class="cropper-actions">
          <button type="button" class="action-btn cancel-btn" @click="$emit('cancel')">إلغاء</button>
          <button type="button" class="action-btn crop-btn" @click="confirmCrop">حدد و أضف</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

export default {
  name: 'ImageCropper',
  props: {
    src: { type: String, required: true },
    aspectRatio: { type: Number, default: null },
    targetWidth: { type: Number, default: null },
    targetHeight: { type: Number, default: null },
    maxWidth: { type: Number, default: null },
    maxHeight: { type: Number, default: null },
    showShapeControls: { type: Boolean, default: false }
  },
  data() {
    return {
      cropper: null
    };
  },
  mounted() {
    this.initCropper();
  },
  beforeUnmount() {
    this.destroyCropper();
  },
  methods: {
    isMobileViewport() {
      return window.innerWidth <= 768;
    },
    fitImageForMobile() {
      if (!this.cropper || !this.isMobileViewport()) return;

      const container = this.cropper.getContainerData();
      const image = this.cropper.getImageData();
      if (!container.width || !container.height || !image.naturalWidth || !image.naturalHeight) return;

      const imageRatio = Math.min(
        container.width / image.naturalWidth,
        container.height / image.naturalHeight
      );

      const fittedWidth = image.naturalWidth * imageRatio;
      const fittedHeight = image.naturalHeight * imageRatio;

      this.cropper.setCanvasData({
        width: fittedWidth,
        height: fittedHeight,
        left: (container.width - fittedWidth) / 2,
        top: (container.height - fittedHeight) / 2
      });

      const cropPadding = 0.88;
      let cropWidth = fittedWidth * cropPadding;
      let cropHeight = fittedHeight * cropPadding;

      if (Number.isFinite(this.aspectRatio) && this.aspectRatio > 0) {
        if (cropWidth / cropHeight > this.aspectRatio) {
          cropWidth = cropHeight * this.aspectRatio;
        } else {
          cropHeight = cropWidth / this.aspectRatio;
        }
      }

      this.cropper.setCropBoxData({
        width: cropWidth,
        height: cropHeight,
        left: (container.width - cropWidth) / 2,
        top: (container.height - cropHeight) / 2
      });
    },
    initCropper() {
      const imgEl = this.$refs.img;
      if (!imgEl) return;

      this.destroyCropper();
      this.cropper = new Cropper(imgEl, {
        aspectRatio: Number.isFinite(this.aspectRatio) ? this.aspectRatio : NaN,
        viewMode: this.isMobileViewport() ? 0 : 1,
        dragMode: 'move',
        autoCropArea: this.isMobileViewport() ? 0.8 : 0.92,
        responsive: true,
        restore: false,
        background: false,
        modal: true,
        guides: true,
        center: false,
        highlight: false,
        movable: true,
        zoomable: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        ready: () => {
          this.fitImageForMobile();
        }
      });
    },
    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    constrainCanvas(canvas) {
      const maxW = Number.isFinite(this.maxWidth) ? this.maxWidth : null;
      const maxH = Number.isFinite(this.maxHeight) ? this.maxHeight : null;
      if (!canvas || (!maxW && !maxH)) return canvas;

      const width = canvas.width;
      const height = canvas.height;
      let scale = 1;

      if (maxW && width > maxW) scale = Math.min(scale, maxW / width);
      if (maxH && height > maxH) scale = Math.min(scale, maxH / height);
      if (scale >= 1) return canvas;

      const resized = document.createElement('canvas');
      resized.width = Math.max(1, Math.round(width * scale));
      resized.height = Math.max(1, Math.round(height * scale));

      const ctx = resized.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, resized.width, resized.height);
      ctx.drawImage(canvas, 0, 0, resized.width, resized.height);

      return resized;
    },
    flattenCanvas(sourceCanvas, backgroundColor = '#ffffff') {
      if (!sourceCanvas) return sourceCanvas;

      const canvas = document.createElement('canvas');
      canvas.width = sourceCanvas.width;
      canvas.height = sourceCanvas.height;

      const context = canvas.getContext('2d');
      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(sourceCanvas, 0, 0);

      return canvas;
    },
    confirmCrop() {
      if (!this.cropper) {
        this.$emit('confirm', { blob: null, dataUrl: this.src });
        return;
      }

      const hasTargetW = Number.isFinite(this.targetWidth);
      const hasTargetH = Number.isFinite(this.targetHeight);
      const canvasOptions = hasTargetW && hasTargetH
        ? { width: this.targetWidth, height: this.targetHeight, fillColor: '#ffffff' }
        : { fillColor: '#ffffff' };

      let canvas = this.cropper.getCroppedCanvas(canvasOptions);
      canvas = this.constrainCanvas(canvas);
      canvas = this.flattenCanvas(canvas);

      if (!canvas) {
        this.$emit('confirm', { blob: null, dataUrl: this.src });
        return;
      }

      const mimeType = 'image/jpeg';
      const dataUrl = canvas.toDataURL(mimeType, 0.92);

      canvas.toBlob(
        (blob) => {
          this.$emit('confirm', { blob, dataUrl });
        },
        mimeType,
        0.92
      );
    }
  }
};
</script>

<style scoped>
.cropper-page {
  position: fixed;
  inset: 0;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 2147483647;
}

.cropper-card {
  width: min(1100px, 100%);
  height: min(820px, calc(100vh - 48px));
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.cropper-header {
  text-align: center;
}

.cropper-title {
  margin: 0;
  font-size: 28px;
  color: #111827;
}

.cropper-subtitle {
  margin: 10px 0 0;
  color: #6b7280;
  font-size: 15px;
}

.cropper-stage {
  flex: 1;
  min-height: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  overflow: hidden;
  padding: 18px;
}

.cropper-image {
  display: block;
  max-width: 100%;
}

.cropper-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.action-btn {
  min-width: 140px;
  height: 48px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  transition: 0.2s ease;
}

.cancel-btn {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.crop-btn {
  background: #5bc6d0;
  color: #ffffff;
  border: 1px solid #5bc6d0;
}

.cancel-btn:hover {
  background: #e5e7eb;
}

.crop-btn:hover {
  background: #43b7c2;
  border-color: #43b7c2;
}

:deep(.cropper-container) {
  width: 100% !important;
  height: 100% !important;
  background: #ffffff !important;
}

:deep(.cropper-wrap-box),
:deep(.cropper-canvas),
:deep(.cropper-drag-box),
:deep(.cropper-bg) {
  background: #ffffff !important;
  opacity: 1 !important;
}

:deep(.cropper-modal) {
  opacity: 1 !important;
  background: rgba(255, 255, 255, 0.14) !important;
  backdrop-filter: blur(7px) !important;
  -webkit-backdrop-filter: blur(7px) !important;
}

:deep(.cropper-face) {
  background: rgba(255, 255, 255, 0.08) !important;
  opacity: 1 !important;
}

:deep(.cropper-view-box) {
  background: transparent !important;
  outline: 2px solid #5bc6d0 !important;
  box-shadow: none !important;
}

:deep(.cropper-line),
:deep(.cropper-point) {
  background-color: #5bc6d0 !important;
}

@media (max-width: 768px) {
  .cropper-page {
    padding: 12px;
  }

  .cropper-card {
    height: calc(100vh - 24px);
    padding: 18px;
    border-radius: 14px;
  }

  .cropper-title {
    font-size: 22px;
  }

  .cropper-subtitle {
    font-size: 13px;
  }

  .cropper-stage {
    padding: 8px;
    min-height: 52vh;
  }

  .action-btn {
    min-width: 120px;
    height: 44px;
  }
}
</style>
